import { Coins, LogOut, Plus, Settings, User } from "lucide-react";
import type React from "react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { useAuth, useCredit } from "@/features/auth";
import { useProjectList } from "@/features/project";
import { useUI } from "@/features/ui";
import { ProjectItem } from "./ProjectItem";

export const ProjectsPanel: React.FC = () => {
  const { showProjects, setShowProjects } = useUI();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { credit, fetchCredit } = useCredit();
  const { currentProjectId, projects, deleteProject, renameProject } = useProjectList();
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  const handleAccountMenuToggle = useCallback(() => {
    const nextState = !showAccountMenu;
    setShowAccountMenu(nextState);
    if (nextState) {
      fetchCredit();
    }
  }, [showAccountMenu, fetchCredit]);

  const handleSelect = useCallback(
    (projectId: string) => {
      if (projectId !== currentProjectId) {
        navigate(`/${projectId}`);
      }
    },
    [currentProjectId, navigate],
  );

  return (
    <>
      {/* Mobile overlay backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 sbs:hidden transition-opacity duration-300 ${showProjects ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setShowProjects(false)}
        onKeyDown={(e) => e.key === "Escape" && setShowProjects(false)}
        role="button"
        tabIndex={showProjects ? 0 : -1}
        aria-label="Close projects panel"
      />
      {/* Panel - fixed overlay on mobile, inline on desktop */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 flex flex-col h-full border-r border-slate-800 bg-slate-900 overflow-hidden transition-all duration-300 ease-out sbs:relative sbs:z-auto ${showProjects ? "translate-x-0 sbs:w-64" : "-translate-x-full sbs:translate-x-0 sbs:w-0"}`}
      >
        <div className="p-3 border-b border-slate-800 flex items-center gap-2">
          <button
            onClick={() => navigate("/")}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-medium transition-colors"
          >
            <Plus size={14} />
            New Project
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {projects.map((project) => (
            <ProjectItem
              key={project.id}
              project={project}
              isActive={project.id === currentProjectId}
              onSelect={() => handleSelect(project.id)}
              onDelete={() => deleteProject(project.id)}
              onRename={(newTitle) => renameProject(project.id, newTitle)}
            />
          ))}
        </div>

        <div className="relative border-t border-slate-800 p-3">
          <button
            type="button"
            onClick={handleAccountMenuToggle}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-800 transition-colors text-left"
          >
            {user.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
                <User size={14} className="text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-200 truncate">
                {user.name || user.email || "User"}
              </div>
              {user.name && user.email && (
                <div className="text-[10px] text-slate-500 truncate">{user.email}</div>
              )}
            </div>
          </button>

          {showAccountMenu && (
            <div className="absolute bottom-full left-3 right-3 mb-1 bg-slate-800 rounded-lg shadow-lg border border-slate-700 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-300 border-b border-slate-700">
                <Coins size={14} />
                <span>Credit: {credit?.totalBalance ?? "..."}</span>
              </div>
              {user.isAdmin && (
                <button
                  type="button"
                  onClick={() => navigate("/admin")}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 transition-colors"
                >
                  <Settings size={14} />
                  Admin
                </button>
              )}
              <button
                type="button"
                onClick={logout}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 transition-colors"
              >
                <LogOut size={14} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
